/**
 * The browser's timezone, carried into sign-up.
 *
 * Signing up captures a username, an email and a password. Nothing else — so
 * until 2026-09-08 a brand-new account had no timezone at all, and everything
 * that decides what "today" means fell back to UTC: the day rolled over at the
 * wrong hour, and the day-start plan was built at the wrong time. Onboarding
 * asks for the zone, but only for people who complete it.
 *
 * The browser already knows the answer, for free, at the moment they sign up.
 * The server validates whatever arrives against its own tz database and only
 * ever FILLS a blank — a zone someone has chosen is never overwritten by the
 * machine they happen to be signing in from.
 */

/** The IANA zone this browser reports, or null if it will not say. */
export function detectedTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null            // ancient or locked-down browser; the server copes
  }
}

/**
 * Add `tz` to a URL we are about to navigate to.
 *
 * Google sign-in is a full-page redirect, so this is the only way the zone can
 * reach the server: it rides out on the URL that starts the round trip and
 * waits in the session until Google sends the person back.
 */
export function withTimezone(url: string): string {
  const tz = detectedTimezone()
  if (!tz) return url
  return `${url}${url.includes('?') ? '&' : '?'}tz=${encodeURIComponent(tz)}`
}
